import type { AccountApiConnections } from '../types/account.js';
import {
  API_CONNECTION_PLATFORMS,
  type ApiConnectionPlatform,
} from '../types/apiConnections.js';

// The ONE mapping between the api-connections resolution (Zernio SocialAccount
// `platform` ids: metaads / facebook / instagram / googlebusiness / googleads /
// tiktok) and the STORED Account.apiConnections boolean fields. Shared by every
// write path (finalize, detail-load persist, manual refresh) and every read
// path (grid cells, filters) so the two shapes can never drift.

export type AccountConnectionField = Exclude<keyof AccountApiConnections, 'lastCheckedAt'>;

export const PLATFORM_TO_FIELD: Record<ApiConnectionPlatform, AccountConnectionField> = {
  metaads: 'metaAds',
  facebook: 'facebookPage',
  instagram: 'instagram',
  googlebusiness: 'googleBusiness',
  googleads: 'googleAds',
  tiktok: 'tiktok',
};

// Build a stored block from the resolved platform-presence map + the check time
// (the ISO timestamp Zernio was actually queried at).
export function toStoredApiConnections(
  platforms: Record<ApiConnectionPlatform, boolean>,
  checkedAt: string | null,
): AccountApiConnections {
  const out = { lastCheckedAt: checkedAt } as AccountApiConnections;
  for (const platform of API_CONNECTION_PLATFORMS) {
    out[PLATFORM_TO_FIELD[platform]] = platforms[platform] === true;
  }
  return out;
}

// Read one platform's stored boolean. null = never checked (the whole block is
// null), distinct from `false` (checked, not connected). Tolerates undefined
// (legacy docs without the field).
export function storedConnectionForPlatform(
  conn: AccountApiConnections | null | undefined,
  platform: ApiConnectionPlatform,
): boolean | null {
  if (!conn) return null;
  return conn[PLATFORM_TO_FIELD[platform]];
}

// Build the block to persist right after a successful onboarding connect.
// metaAds + facebookPage are authoritatively TRUE here (an ad account was
// scoped and a Page selected), so they're forced on even when the live
// resolution failed (platforms null); instagram follows the finalize IG pick;
// other platforms come from the live resolution when available.
export function finalizeConnectionsBlock(
  platforms: Record<ApiConnectionPlatform, boolean> | null,
  opts: { instagram: boolean; checkedAt: string },
): AccountApiConnections {
  const block = platforms
    ? toStoredApiConnections(platforms, opts.checkedAt)
    : toStoredApiConnections(
        {
          metaads: false,
          facebook: false,
          instagram: false,
          googlebusiness: false,
          googleads: false,
          tiktok: false,
        },
        opts.checkedAt,
      );
  block.metaAds = true;
  block.facebookPage = true;
  if (opts.instagram) block.instagram = true;
  return block;
}

// Normalize a raw stored value (legacy docs lack the field; a present block may
// have missing/extra keys) into a clean block or null. Used on every Account
// read so consumers always see an explicit block-or-null (never undefined).
export function normalizeStoredApiConnections(raw: unknown): AccountApiConnections | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return {
    metaAds: r.metaAds === true,
    facebookPage: r.facebookPage === true,
    instagram: r.instagram === true,
    googleBusiness: r.googleBusiness === true,
    googleAds: r.googleAds === true,
    tiktok: r.tiktok === true,
    lastCheckedAt: typeof r.lastCheckedAt === 'string' ? r.lastCheckedAt : null,
  };
}
